import { useState, useEffect, useCallback } from 'react';

const StepTypeSelector = ({ onSelect, initialValues = {}, isBarSupply = false, barLength = null }) => {
    const [selectedType, setSelectedType] = useState(initialValues.stepType || null);
    const [action, setAction] = useState(null);
    const [customStepValue, setCustomStepValue] = useState(initialValues.customStepValue || '');
    const [showCustomInput, setShowCustomInput] = useState(false);

    // Define handleSubmit first before using it in useEffect
    const handleSubmit = useCallback(() => {
        if (selectedType === 'standard') {
            onSelect({
                stepType: selectedType,
                action: 'finish',
                customStepValue: null
            });
        } else if (selectedType === 'custom' && customStepValue) {
            onSelect({
                stepType: selectedType,
                action: 'finish',
                customStepValue: customStepValue
            });
        }
    }, [selectedType, customStepValue, onSelect]);

    // Automatically set action to 'finish' when 'standard' is selected
    useEffect(() => {
        if (selectedType === 'standard') {
            setAction('finish');
        }
    }, [selectedType]);

    // Automatically submit when standard is selected (since action is set to 'finish' automatically)
    useEffect(() => {
        if (selectedType === 'standard' && action === 'finish') {
            handleSubmit();
        }
    }, [selectedType, action, handleSubmit]);

    // Show custom input field when custom is selected
    useEffect(() => {
        if (selectedType === 'custom') {
            setShowCustomInput(true);
        } else {
            setShowCustomInput(false);
        }
    }, [selectedType]);

    // If we have initial values and it's custom, show the input field
    useEffect(() => {
        if (initialValues.stepType === 'custom' && initialValues.customStepValue) {
            setShowCustomInput(true);
        }
    }, [initialValues]);

    return (
        <div className="space-y-6">
            {/* Step Type Selection */}
            <div className="space-y-4">
                <button
                    onClick={() => setSelectedType('standard')}
                    className={`w-full p-4 border-2 rounded-lg text-center
            ${selectedType === 'standard' ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                >
                    <div className="inline-block px-3 py-1 bg-red-100 text-red-600 rounded-full text-sm mb-2">
                        PASSO STANDARD
                    </div>
                </button>

                <button
                    onClick={() => setSelectedType('custom')}
                    className={`w-full p-4 border-2 rounded-lg text-center text-gray-500
            ${selectedType === 'custom' ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                >
                    <div className="text-sm">
                        PASSO PERSONALIZZATO
                    </div>
                </button>
            </div>

            {/* Custom Step Input Field */}
            {showCustomInput && (
                <div className="pt-4 space-y-4">
                    {isBarSupply && selectedType === 'custom' ? (
                        <>
                            {/* For bar supply, show only progettazione option */}
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <p className="text-sm text-yellow-800 mb-3">
                                    <strong>Nota:</strong> Per il passo personalizzato, il numero, la posizione ed il costo dei fori o fresature devono essere definiti in fase di progettazione.
                                </p>
                                <button
                                    onClick={() => {
                                        setCustomStepValue('progettazione');
                                        onSelect({
                                            stepType: 'custom',
                                            action: 'progettazione',
                                            customStepValue: 'progettazione'
                                        });
                                    }}
                                    className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                >
                                    Vai a Progettazione
                                </button>
                            </div>
                        </>
                    ) : (
                        // Regular custom step input for non-bar supply
                        <>
                            <label htmlFor="customStep" className="block text-sm font-medium text-gray-700 mb-2">
                                Inserisci il valore del passo personalizzato:
                            </label>
                            <input
                                type="number"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                id="customStep"
                                value={customStepValue}
                                onChange={(e) => setCustomStepValue(e.target.value)}
                                placeholder="Valore del passo in mm"
                                className="w-full p-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                        </>
                    )}
                </div>
            )}

            {/* Submit Button - Only shown for custom type since standard auto-submits */}
            {selectedType === 'custom' && (
                <div className="pt-4">
                    <button
                        onClick={handleSubmit}
                        disabled={!selectedType || (selectedType === 'custom' && !customStepValue)}
                        className="w-full py-2 px-4 bg-red-500 text-white rounded-lg
                       disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Avanti
                    </button>
                </div>
            )}
        </div>
    );
};

export default StepTypeSelector;
